/**
 * Recursively converts null to undefined in plain objects and arrays.
 * Use at the boundary where Zod-validated data (which may include null from .nullish())
 * is passed into domain types that use optional properties (T | undefined only).
 */

export type NullToUndefined<T> = T extends null
  ? undefined
  : T extends (infer U)[]
    ? NullToUndefined<U>[]
    : T extends object
      ? { [K in keyof T]: NullToUndefined<T[K]> }
      : T;

export function nullToUndefined<T>(value: T): NullToUndefined<T> {
  if (value === null) {
    return undefined as NullToUndefined<T>;
  }
  if (Array.isArray(value)) {
    return value.map((item) => nullToUndefined(item)) as NullToUndefined<T>;
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = v === null ? undefined : nullToUndefined(v);
    }
    return out as NullToUndefined<T>;
  }
  return value as NullToUndefined<T>;
}
